import type { FC } from "react";
import React, { useState } from "react";

import { bindArgs } from "@core/foundation/function";
import { singleline } from "@core/foundation/string";

import { toast } from "@react/ui-primitives/extension";

import { CaptureBar, Header } from "../features/app-layout/index.ts";
import {
  UnrecognizedBarcodeDialog,
  useBarcodeLookupActions,
} from "../features/barcode-lookup/index.ts";
import { useBarcodeRecognitionActions } from "../features/barcode-recognition/index.ts";
import { ManualEntrySheet } from "../features/manual-entry/index.ts";
import { PlacementGrid } from "../features/placement-grid/index.ts";
import { PlacementQueue } from "../features/placement-queue/index.ts";
import { ReceiptCaptureSheet } from "../features/receipt-capture/index.ts";
import { useLogEntryActions } from "../shared/hooks/useLogEntryActions.ts";

export const Home: FC = () => {
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);

  const [isReceiptCaptureOpen, setIsReceiptCaptureOpen] = useState(false);

  const [unrecognizedBarcode, setUnrecognizedBarcode] = useState<null | string>(
    null,
  );

  const { scanBarcode } = useBarcodeRecognitionActions();
  const { lookupBarcode } = useBarcodeLookupActions();
  const { createLogEntry } = useLogEntryActions();
  const handleScanBarcode = (): void => {
    scanBarcode.mutate(undefined, {
      onSuccess: ({ barcode: scanned }) => {
        lookupBarcode.mutate(scanned, {
          onSuccess: result => {
            switch (result.status) {
              case "found":
                createLogEntry.mutate({
                  name: result.name,
                  product: { barcode: result.barcode, type: "barcode" },
                  totalCalories: result.totalCalories,
                  weightGrams: result.weightGrams,
                });

                return;
              case "invalid":
                toast.warning(singleline`
                  "${result.scanned}" isn't a valid product barcode. Scan the
                  whole barcode.
                `);

                return;
              case "unknown":
                setUnrecognizedBarcode(result.barcode);
                break;
            }
          },
        });
      },
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <Header />
      <PlacementQueue />
      <PlacementGrid onScanBarcode={handleScanBarcode} />
      <CaptureBar
        onAddManual={bindArgs(setIsManualEntryOpen, true)}
        onScanBarcode={handleScanBarcode}
        onScanReceipt={bindArgs(setIsReceiptCaptureOpen, true)}
      />
      <ManualEntrySheet
        onOpenChange={setIsManualEntryOpen}
        open={isManualEntryOpen}
      />
      <ReceiptCaptureSheet
        onOpenChange={setIsReceiptCaptureOpen}
        open={isReceiptCaptureOpen}
      />
      <UnrecognizedBarcodeDialog
        barcode={unrecognizedBarcode}
        onClose={bindArgs(setUnrecognizedBarcode, null)}
      />
    </div>
  );
};
